import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import type { Nullable } from "primereact/ts-helpers";
import { useEffect, useState } from "react";
import { checkIsTextFieldType } from "../../../../../helpers/check-schema-field-type";
import type {
  SchemaField,
  SchemaFieldOption,
  SchemaFieldTypes,
} from "../../../../../helpers/format-databases";
import Spinner from "../../../../../ui/components/utils/Spinner/Spinner";
import { SelectSchemaFieldType } from "../../SelectSchemaFieldType/SelectSchemaFieldType";

import type { ModalDialogProps } from "../../../../../ui/shared/global-tools/dialog-system";
import "./EditFieldDialog.scss";

export interface EditFieldDialogProps extends ModalDialogProps<void> {
  closeDialog: () => void;
  parameter: EditFieldDialogParameter;
}

export interface EditFieldDialogParameter {
  edit: (editedField: SchemaField) => void;
  schemaFieldTypes: SchemaFieldTypes;
  schemaFields: SchemaField[];
  fieldToEdit: SchemaField;
}

export default function makeEditFieldDialog({
  closeDialog,
  parameter: { edit, fieldToEdit, schemaFieldTypes, schemaFields },
}: EditFieldDialogProps) {
  const [label, setLabel] = useState("");
  const [required, setRequired] = useState(true);
  const [name, setName] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [type, setType] = useState<string>();
  const [options, setOptions] = useState<SchemaFieldOption[]>();
  const [fieldLength, setFieldLength] = useState<Nullable<number | null>>();
  const [errors, setErrors] = useState<Map<string, string>>(new Map());

  const editField = () => {
    if (!type) return;
    edit({
      label,
      name,
      type,
      default: defaultValue === "" ? undefined : defaultValue,
      length: fieldLength === null ? undefined : fieldLength,
      options,
      required,
    });
    closeDialog();
  };
  const onNameChange = (val: string) => {
    setName(val);
    const found = schemaFields.find((f) => f.name === val);
    const oldErrors = new Map(errors);
    if (found && fieldToEdit.name !== val) {
      oldErrors.set("name", "Ce nom est déjà pris");
    } else {
      oldErrors.delete("name");
    }
    setErrors(oldErrors);
  };

  useEffect(() => {
    setType(fieldToEdit.type);
    if (fieldToEdit.default) setDefaultValue(fieldToEdit.default);
    if (fieldToEdit.length) setFieldLength(fieldToEdit.length);
    setLabel(fieldToEdit.label);
    setName(fieldToEdit.name);
    if (fieldToEdit.options) setOptions([...fieldToEdit.options]);
    setRequired(fieldToEdit.required);
  }, []);

  const content = (
    <>
      {!type ? (
        <Spinner />
      ) : (
        <div className="EditFieldDialog Card mt-4">
          <h2 className="EditFieldDialog-title">Configuration du référentiel</h2>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              editField();
            }}
            className="EditFieldDialog-form"
          >
            <div className="EditFieldDialog-inputGroup">
              <label className="Field full">
                <span className="Field-label">Libellé du champ</span>
                <InputText
                  className="Field-input"
                  placeholder="Champ requis *"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  required
                />
              </label>

              <label className="Field full">
                <span className="Field-label">Est obligatoire?</span>
                <div className="Field-radioButtonContainer ">
                  <label className="RadioButton">
                    <RadioButton
                      className="RadioButton-input"
                      name="yes"
                      value={true}
                      onChange={(e) => setRequired(e.value)}
                      checked={required}
                    />
                    <span className="RadioButton-label">Oui</span>
                  </label>
                  <label className="RadioButton">
                    <RadioButton
                      className="RadioButton-input"
                      name="no"
                      value={false}
                      onChange={(e) => setRequired(e.value)}
                      checked={!required}
                    />
                    <span className="RadioButton-label">Non</span>
                  </label>
                </div>
              </label>

              <label className="Field full">
                <span className="Field-label">Valeur par défaut</span>
                <InputText
                  className="Field-input"
                  value={defaultValue}
                  onChange={(e) => setDefaultValue(e.target.value)}
                />
              </label>
            </div>

            <div className="flex justify-content-between gap-5">
              <label className="Field w-full">
                <span className="Field-label">Nom du champ</span>
                <InputText
                  className="Field-input w-full"
                  placeholder="Champ requis *"
                  value={name}
                  onChange={(e) => onNameChange(e.target.value.toLowerCase())}
                  required
                  invalid={!!errors.get("name")}
                />
                <small className="Field-errorMessage">{errors.get("name")}</small>
              </label>

              {checkIsTextFieldType(type) && (
                <label className="Field w-full">
                  <span className="Field-label">Longueur du champ</span>
                  <InputNumber
                    className="Field-inputNumber w-full"
                    value={fieldLength}
                    onValueChange={(e) => setFieldLength(e.value)}
                  />
                </label>
              )}
            </div>

            <SelectSchemaFieldType
              schemaFieldTypes={schemaFieldTypes}
              type={type}
              fieldLength={fieldLength}
              options={options}
              onChange={(val) => {
                setOptions(val.options);
                setType(val.type);
                setFieldLength(val.fieldLength);
              }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "36.37px",
              }}
            >
              <Button
                type="submit"
                className="Btn primary"
                label="Editer le champ"
                disabled={errors.size > 0}
              />
            </div>
          </form>
        </div>
      )}
    </>
  );

  return { header: "Editer le champ", content };
}
